import { useState, useMemo, useEffect } from 'react';
import { useFuncionarios } from '@/context/FuncionariosContext';
import { supabase } from '@/lib/supabase';
import AnularProgressaoModal from './AnularProgressaoModal';

interface ProgressaoRow {
  id: string;
  funcionarioId: string;
  funcionarioNome: string;
  carreira: string;
  dataProgressao: string;
  nivelAnterior: number;
  nivelNovo: number;
  pontosDescontados: number;
  observacoes: string | null;
  aceleradorRemuneratorio: boolean;
  createdAt: string;
}

const avatarColors = [
  'bg-rose-100 text-rose-700',
  'bg-violet-100 text-violet-700',
  'bg-amber-100 text-amber-700',
  'bg-teal-100 text-teal-700',
  'bg-sky-100 text-sky-700',
  'bg-orange-100 text-orange-700',
];

function getAvatarColor(id: string) {
  const idx = parseInt(id.replace(/\D/g, ''), 10) % avatarColors.length;
  return avatarColors[idx];
}

function getInitials(nome: string) {
  const parts = nome.split(' ').filter((_, i, arr) => i === 0 || i === arr.length - 1);
  return parts.map((n) => n[0]).join('');
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const [year, month, day] = dateStr.split('-');
  if (!year || !month || !day) return dateStr;
  return `${day}/${month}/${year}`;
}

type ProgSortKey = 'nome' | 'data' | 'nivelAnterior' | 'nivelNovo' | 'pontos' | 'totalProgressoes';
type ProgSortDir = 'asc' | 'desc';

export default function ProgressoesTab() {
  const { funcionarios } = useFuncionarios();
  const [progressoes, setProgressoes] = useState<ProgressaoRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [carreiraFilter, setCarreiraFilter] = useState('');
  const [anoFilter, setAnoFilter] = useState('');
  const [aceleradorFilter, setAceleradorFilter] = useState<'todos' | 'sim' | 'nao'>('todos');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [anularProgressao, setAnularProgressao] = useState<ProgressaoRow | null>(null);
  const [sortKey, setSortKey] = useState<ProgSortKey>('data');
  const [sortDir, setSortDir] = useState<ProgSortDir>('desc');

  useEffect(() => {
    async function load() {
      setLoading(true);
      const { data, error } = await supabase
        .from('progressoes')
        .select('*')
        .order('data_progressao', { ascending: false });

      if (!error && data) {
        const funcMap: Record<string, { nome: string; carreira: string }> = {};
        funcionarios.forEach((f) => { funcMap[f.id] = { nome: f.nome, carreira: f.carreira }; });

        setProgressoes(
          data.map((row) => ({
            id: row.id as string,
            funcionarioId: row.funcionario_id as string,
            funcionarioNome: funcMap[row.funcionario_id as string]?.nome ?? row.funcionario_id as string,
            carreira: funcMap[row.funcionario_id as string]?.carreira ?? '—',
            dataProgressao: row.data_progressao as string,
            nivelAnterior: row.nivel_anterior as number,
            nivelNovo: row.nivel_novo as number,
            pontosDescontados: Number(row.pontos_descontados),
            observacoes: row.observacoes as string | null,
            aceleradorRemuneratorio: !!row.acelerador_remuneratorio,
            createdAt: row.created_at as string,
          }))
        );
      }
      setLoading(false);
    }
    load();
  }, [funcionarios]);

  const carreiras = useMemo(() => Array.from(new Set(progressoes.map((p) => p.carreira))).sort(), [progressoes]);

  const anos = useMemo(() => {
    const set = new Set<string>();
    progressoes.forEach((p) => {
      const ano = p.dataProgressao?.split('-')[0];
      if (ano) set.add(ano);
    });
    return Array.from(set).sort((a, b) => Number(b) - Number(a));
  }, [progressoes]);

  const progressoesPorFunc = useMemo(() => {
    const map: Record<string, number> = {};
    progressoes.forEach((p) => { map[p.funcionarioId] = (map[p.funcionarioId] ?? 0) + 1; });
    return map;
  }, [progressoes]);

  function handleSort(key: ProgSortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'data' ? 'desc' : 'asc');
    }
  }

  const filtered = useMemo(() => {
    const base = progressoes.filter((p) => {
      const matchSearch = search === '' || p.funcionarioNome.toLowerCase().includes(search.toLowerCase()) || p.funcionarioId.includes(search);
      const matchCarreira = carreiraFilter === '' || p.carreira === carreiraFilter;
      const matchAno = anoFilter === '' || p.dataProgressao?.startsWith(anoFilter);
      const matchAcelerador = aceleradorFilter === 'todos' || (aceleradorFilter === 'sim' ? p.aceleradorRemuneratorio : !p.aceleradorRemuneratorio);
      return matchSearch && matchCarreira && matchAno && matchAcelerador;
    });

    return [...base].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'nome': cmp = a.funcionarioNome.localeCompare(b.funcionarioNome); break;
        case 'data': cmp = (a.dataProgressao ?? '').localeCompare(b.dataProgressao ?? ''); break;
        case 'nivelAnterior': cmp = a.nivelAnterior - b.nivelAnterior; break;
        case 'nivelNovo': cmp = a.nivelNovo - b.nivelNovo; break;
        case 'pontos': cmp = a.pontosDescontados - b.pontosDescontados; break;
        case 'totalProgressoes': cmp = (progressoesPorFunc[a.funcionarioId] ?? 0) - (progressoesPorFunc[b.funcionarioId] ?? 0); break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [progressoes, search, carreiraFilter, anoFilter, sortKey, sortDir, progressoesPorFunc]);

  const totalPontosDescontados = progressoes.reduce((s, p) => s + p.pontosDescontados, 0);
  const funcComMultiplas = Object.values(progressoesPorFunc).filter((c) => c > 1).length;
  const hasFilters = search || carreiraFilter || anoFilter || aceleradorFilter !== 'todos';

  function clearFilters() { setSearch(''); setCarreiraFilter(''); setAnoFilter(''); setAceleradorFilter('todos'); }

  const progHeaders: { key: ProgSortKey | null; label: string }[] = [
    { key: 'nome', label: 'Funcionário' },
    { key: 'data', label: 'Data' },
    { key: null, label: 'Progressão de Nível' },
    { key: 'pontos', label: 'Pts Descontados' },
    { key: 'totalProgressoes', label: 'Nº Progressões' },
    { key: null, label: 'Observações' },
    { key: null, label: 'Acelerador' },
    { key: null, label: 'Ações' },
  ];

  if (loading) {
    return (
      <div className="py-20 flex flex-col items-center gap-3 text-slate-400">
        <div className="w-8 h-8 flex items-center justify-center"><i className="ri-loader-4-line text-3xl animate-spin" /></div>
        <p className="text-sm">A carregar progressões...</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-5">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: 'ri-arrow-up-circle-line', value: progressoes.length, label: 'Total de Progressões', iconBg: 'bg-emerald-50 text-emerald-600', accent: 'border-l-emerald-400' },
            { icon: 'ri-team-line', value: Object.keys(progressoesPorFunc).length, label: 'Funcionários com Progressão', iconBg: 'bg-slate-100 text-slate-600', accent: 'border-l-slate-400' },
            { icon: 'ri-repeat-line', value: funcComMultiplas, label: 'Com Múltiplas Progressões', iconBg: 'bg-amber-50 text-amber-600', accent: 'border-l-amber-400' },
            { icon: 'ri-subtract-line', value: `${totalPontosDescontados} pts`, label: 'Total Pontos Descontados', iconBg: 'bg-rose-50 text-rose-500', accent: 'border-l-rose-400' },
          ].map((s) => (
            <div key={s.label} className={`bg-white rounded-xl border border-slate-100 border-l-4 ${s.accent} p-4 flex items-center gap-3`}>
              <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${s.iconBg}`}>
                <i className={`${s.icon} text-lg`} />
              </div>
              <div className="min-w-0">
                <p className="text-xl font-bold text-slate-800 leading-tight">{s.value}</p>
                <p className="text-xs text-slate-500 leading-tight">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <div className="w-9 h-9 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                <i className="ri-search-line text-base" />
              </div>
              <input
                type="text"
                placeholder="Pesquisar por nome ou nº de funcionário..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
              />
            </div>
            <select
              value={anoFilter}
              onChange={(e) => setAnoFilter(e.target.value)}
              className="text-sm border border-slate-200 rounded-lg px-3 py-2.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 cursor-pointer"
            >
              <option value="">Todos os Anos</option>
              {anos.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <select
              value={carreiraFilter}
              onChange={(e) => setCarreiraFilter(e.target.value)}
              className="text-sm border border-slate-200 rounded-lg px-3 py-2.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 cursor-pointer"
            >
              <option value="">Todas as Carreiras</option>
              {carreiras.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              value={aceleradorFilter}
              onChange={(e) => setAceleradorFilter(e.target.value as 'todos' | 'sim' | 'nao')}
              className="text-sm border border-slate-200 rounded-lg px-3 py-2.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 cursor-pointer"
            >
              <option value="todos">Acelerador: Todos</option>
              <option value="sim">Com Acelerador</option>
              <option value="nao">Sem Acelerador</option>
            </select>
          </div>
          {hasFilters && (
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-400">
                A mostrar <span className="font-semibold text-slate-600">{filtered.length}</span> de {progressoes.length} progressão(ões)
              </p>
              <button
                onClick={clearFilters}
                className="whitespace-nowrap flex items-center gap-1 text-xs text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
              >
                <i className="ri-close-circle-line text-sm" />
                Limpar filtros
              </button>
            </div>
          )}
        </div>

        {/* Tabela */}
        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-100 py-16 flex flex-col items-center gap-3 text-slate-400">
            <div className="w-12 h-12 flex items-center justify-center">
              <i className="ri-arrow-up-circle-line text-4xl" />
            </div>
            <p className="text-sm font-medium">
              {hasFilters ? 'Nenhuma progressão encontrada com os filtros aplicados' : 'Nenhuma progressão registada'}
            </p>
            {hasFilters ? (
              <button onClick={clearFilters} className="whitespace-nowrap text-xs text-slate-500 hover:text-slate-800 underline cursor-pointer">Limpar filtros</button>
            ) : (
              <p className="text-xs text-slate-300">As progressões aparecem aqui após serem registadas no separador Home</p>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
            {!hasFilters && (
              <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                <p className="text-xs text-slate-400">
                  <span className="font-semibold text-slate-600">{filtered.length}</span> progressão(ões) · Ordenado por data mais recente
                </p>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[780px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    {progHeaders.map((col, ci) => (
                      <th
                        key={ci}
                        onClick={() => col.key && handleSort(col.key)}
                        className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap select-none group ${
                          col.key ? 'cursor-pointer hover:bg-zinc-100 transition-colors' : ''
                        } ${
                          sortKey === col.key ? 'text-slate-700' : 'text-slate-400'
                        } ${ci === progHeaders.length - 1 ? 'text-right' : ''}`}
                      >
                        <span className="inline-flex items-center gap-1">
                          {col.label}
                          {col.key && (
                            sortKey === col.key
                              ? <i className={`${sortDir === 'asc' ? 'ri-arrow-up-s-fill' : 'ri-arrow-down-s-fill'} text-[13px] text-slate-700`} />
                              : <i className="ri-expand-up-down-line text-[11px] text-slate-300 group-hover:text-slate-400" />
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => {
                    const colorClass = getAvatarColor(p.funcionarioId);
                    const initials = getInitials(p.funcionarioNome);
                    const totalProgressoesFunc = progressoesPorFunc[p.funcionarioId] ?? 1;
                    const isMultipla = totalProgressoesFunc > 1;
                    const isExpanded = expandedId === p.id;

                    return (
                      <>
                        <tr
                          key={p.id}
                          className={`border-b border-slate-50 hover:bg-slate-50 transition-colors ${p.observacoes ? 'cursor-pointer' : ''}`}
                          onClick={() => p.observacoes && setExpandedId(isExpanded ? null : p.id)}
                        >
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${colorClass}`}>
                                {initials}
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-slate-800">{p.funcionarioNome}</p>
                                <p className="text-xs text-slate-400">{p.carreira} · #{p.funcionarioId}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-2">
                              <div className="w-5 h-5 flex items-center justify-center text-slate-400">
                                <i className="ri-calendar-check-line text-sm" />
                              </div>
                              <div>
                                <span className="text-sm text-slate-700 font-medium block">{formatDate(p.dataProgressao)}</span>
                                <span className="text-xs text-slate-400">{p.dataProgressao?.split('-')[0]}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-slate-500">Nível {p.nivelAnterior}</span>
                              <div className="w-5 h-5 flex items-center justify-center text-emerald-500">
                                <i className="ri-arrow-right-line text-sm" />
                              </div>
                              <span className="text-sm font-bold text-emerald-700">Nível {p.nivelNovo}</span>
                              <span className="text-xs bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded-full font-medium">
                                +{p.nivelNovo - p.nivelAnterior}
                              </span>
                            </div>
                          </td>
                          <td className="px-5 py-3.5">
                            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-rose-600 bg-rose-50 px-2.5 py-1 rounded-lg">
                              <i className="ri-subtract-line text-xs" />
                              {p.pontosDescontados % 1 === 0 ? p.pontosDescontados.toFixed(0) : p.pontosDescontados.toFixed(1)} pts
                            </span>
                          </td>
                          <td className="px-5 py-3.5">
                            <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${isMultipla ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                              {isMultipla && <i className="ri-repeat-line text-xs" />}
                              {totalProgressoesFunc}ª progressão
                            </span>
                          </td>
                          <td className="px-5 py-3.5">
                            {p.observacoes ? (
                              <div className="flex items-center gap-1.5 cursor-pointer">
                                <span className="text-xs text-slate-500 italic truncate max-w-[140px]">{p.observacoes}</span>
                                <div className="w-4 h-4 flex items-center justify-center text-slate-400">
                                  <i className={`${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-sm`} />
                                </div>
                              </div>
                            ) : (
                              <span className="text-xs text-slate-300">—</span>
                            )}
                          </td>
                          <td className="px-5 py-3.5">
                            {p.aceleradorRemuneratorio ? (
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                                <i className="ri-flashlight-line text-[10px]" />
                                Sim
                              </span>
                            ) : (
                              <span className="text-xs text-slate-300">—</span>
                            )}
                          </td>
                          <td className="px-5 py-3.5 text-right">
                            <button
                              onClick={(e) => { e.stopPropagation(); setAnularProgressao(p); }}
                              title="Anular progressão e devolver pontos"
                              className="whitespace-nowrap inline-flex items-center gap-1.5 text-xs font-medium text-rose-500 hover:text-rose-700 hover:bg-rose-50 px-2.5 py-1.5 rounded-lg transition-colors cursor-pointer"
                            >
                              <i className="ri-arrow-go-back-line text-sm" />
                              Anular
                            </button>
                          </td>
                        </tr>
                        {isExpanded && p.observacoes && (
                          <tr key={`${p.id}-expanded`} className="bg-amber-50/40 border-b border-slate-100">
                            <td colSpan={7} className="px-5 py-3">
                              <div className="flex items-start gap-2">
                                <div className="w-5 h-5 flex items-center justify-center text-amber-500 flex-shrink-0 mt-0.5">
                                  <i className="ri-file-text-line text-sm" />
                                </div>
                                <div>
                                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Observações</p>
                                  <p className="text-sm text-slate-700 leading-relaxed">{p.observacoes}</p>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
                {filtered.length > 0 && (() => {
                  const totalFiltrado = filtered.reduce((s, p) => s + p.pontosDescontados, 0);
                  const totalProgressoesFiltradas = filtered.length;
                  const funcsFiltradas = new Set(filtered.map((p) => p.funcionarioId)).size;
                  return (
                    <tfoot>
                      <tr className="bg-rose-50/60 border-t-2 border-rose-100">
                        <td className="px-5 py-3.5" colSpan={2}>
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 flex items-center justify-center text-rose-400">
                              <i className="ri-bar-chart-grouped-line text-sm" />
                            </div>
                            <span className="text-xs font-semibold text-slate-600">
                              Resumo do período{hasFilters ? ' filtrado' : ''}
                            </span>
                            <span className="text-xs text-slate-400">
                              · {totalProgressoesFiltradas} progressão(ões) · {funcsFiltradas} funcionário(s)
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5" colSpan={2}>
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 text-sm font-bold text-rose-700 bg-rose-100 px-3 py-1 rounded-lg">
                              <i className="ri-subtract-line text-xs" />
                              {totalFiltrado % 1 === 0 ? totalFiltrado.toFixed(0) : totalFiltrado.toFixed(1)} pts descontados
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5" colSpan={3}>
                          <span className="text-xs text-slate-400 italic">
                            {hasFilters ? 'Total do período selecionado' : 'Total geral'}
                          </span>
                        </td>
                      </tr>
                    </tfoot>
                  );
                })()}
              </table>
            </div>
          </div>
        )}
      </div>

      {anularProgressao && (
        <AnularProgressaoModal
          progressao={anularProgressao}
          onClose={() => setAnularProgressao(null)}
          onSuccess={() => {
            const idAnulado = anularProgressao.id;
            setAnularProgressao(null);
            setProgressoes((prev) => prev.filter((p) => p.id !== idAnulado));
          }}
        />
      )}
    </>
  );
}
